<template>
  <div>
    <v-dialog
      v-model="status"
      :width="width"
      persistent
    >
      <v-card class="campaign-detail-card">
        <!-- Header -->
        <div class="detail-header pa-5">
          <div class="d-flex align-center">
            <div>
              <p class="text-xs text--secondary mb-0">
                Campanha #{{ campaign.id }}
              </p>
              <h3 class="text-h6 font-weight-bold mb-0">
                {{ campaign.name }}
              </h3>
            </div>
            <v-spacer />
            <v-chip
              :color="resolveCampaignStatusColor(campaign.internalStatus)"
              small
              label
              class="font-weight-medium mr-3"
            >
              <span class="white--text">
                {{ resolveCampaignStatusLabel(campaign.internalStatus) }}
              </span>
            </v-chip>
            <v-btn
              icon
              small
              @click="$emit('close')"
            >
              <v-icon color="error">
                {{ icons.mdiClose }}
              </v-icon>
            </v-btn>
          </div>
        </div>

        <v-divider />

        <v-card-text class="pt-5 pb-4">
          <!-- Row 1: main info -->
          <v-row dense>
            <v-col
              cols="6"
              md="3"
              sm="6"
            >
              <div class="info-field">
                <span class="info-label">Serviço</span>
                <span
                  class="info-value font-weight-medium"
                  :style="{ color: resolveServiceChipColor(campaign.serviceId) }"
                >
                  {{ resolveServiceName(campaign.serviceId) }}
                </span>
              </div>
            </v-col>
            <v-col
              cols="6"
              md="3"
              sm="6"
            >
              <div class="info-field">
                <span class="info-label">Cliente</span>
                <span class="info-value font-weight-medium">{{ campaign.userName }}</span>
              </div>
            </v-col>
            <v-col
              cols="6"
              md="3"
              sm="6"
            >
              <div class="info-field">
                <span class="info-label">Data Envio</span>
                <span class="info-value">
                  {{ $day(campaign.sendDate).format('DD/MM/YYYY') }}
                  <span class="text--secondary mx-1">·</span>
                  {{ $day(campaign.sendDate).format('HH:mm') }}
                </span>
              </div>
            </v-col>
            <v-col
              cols="6"
              md="3"
              sm="6"
            >
              <div class="info-field">
                <span class="info-label">Rota</span>
                <span class="info-value text-capitalize">{{ campaign.route || '—' }}</span>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <!-- Row 2: financial & quantities -->
          <v-row dense>
            <v-col
              cols="6"
              md="3"
              sm="6"
            >
              <div class="info-field">
                <span class="info-label">Preço (R$)</span>
                <span class="info-value font-weight-semibold primary--text">
                  {{ $money(campaign.value, 3) }}
                </span>
              </div>
            </v-col>
            <v-col
              cols="6"
              md="3"
              sm="6"
            >
              <div class="info-field">
                <span class="info-label">Qtd Números</span>
                <span class="info-value">{{ $money(campaign.quantity, 0) }}</span>
              </div>
            </v-col>
            <v-col
              cols="6"
              md="3"
              sm="6"
            >
              <div class="info-field">
                <span class="info-label">{{ resolveLabel(campaign.serviceId).validQuantity }}</span>
                <span class="info-value success--text font-weight-medium">
                  {{ $money(campaign.validQuantity, 0) }}
                </span>
              </div>
            </v-col>
            <v-col
              cols="6"
              md="3"
              sm="6"
            >
              <div class="info-field">
                <span class="info-label">{{ resolveLabel(campaign.serviceId).invalidQuantity }}</span>
                <span class="info-value error--text font-weight-medium">
                  {{ $money(campaign.invalidQuantity, 0) }}
                </span>
              </div>
            </v-col>
          </v-row>

          <!-- SMS details -->
          <template v-if="getService() === 'sms' && detail.id">
            <v-divider class="my-4" />
            <p class="text-xs text--secondary font-weight-medium text-uppercase mb-3">
              Detalhes SMS
            </p>
            <v-row dense>
              <v-col
                cols="6"
                md="3"
                sm="6"
              >
                <div class="info-field">
                  <span class="info-label">ID Template</span>
                  <div
                    class="d-flex align-center"
                    style="gap: 8px;"
                  >
                    <span class="info-value font-weight-semibold">
                      {{ detail.smsTemplateId || '—' }}
                    </span>
                    <a
                      v-if="detail.smsTemplateId"
                      class="text-xs primary--text"
                      style="cursor: pointer; white-space: nowrap;"
                      @click="openSmsContent(detail.smsTemplateId)"
                    >
                      Ver mensagem
                    </a>
                  </div>
                </div>
              </v-col>
              <v-col
                cols="6"
                md="3"
                sm="6"
              >
                <div class="info-field">
                  <span class="info-label">Tipo</span>
                  <span class="info-value">{{ detail.type || '—' }}</span>
                </div>
              </v-col>
              <v-col
                cols="6"
                md="3"
                sm="6"
              >
                <div class="info-field">
                  <span class="info-label">Interação</span>
                  <v-chip
                    x-small
                    :color="detail.hasInteraction ? 'success' : 'default'"
                  >
                    {{ detail.hasInteraction ? 'Sim' : 'Não' }}
                  </v-chip>
                </div>
              </v-col>
              <v-col
                cols="6"
                md="3"
                sm="6"
              >
                <div class="info-field">
                  <span class="info-label">Variável</span>
                  <v-chip
                    x-small
                    :color="detail.smsVariable ? 'info' : 'default'"
                  >
                    {{ detail.smsVariable ? 'Sim' : 'Não' }}
                  </v-chip>
                </div>
              </v-col>
            </v-row>
          </template>

          <!-- Voice details -->
          <template v-if="getService() === 'voice'">
            <v-divider class="my-4" />
            <p class="text-xs text--secondary font-weight-medium text-uppercase mb-3">
              Detalhes Voz
            </p>
            <v-row dense>
              <v-col
                cols="6"
                md="3"
                sm="6"
              >
                <div class="info-field">
                  <span class="info-label">Tipo Voz</span>
                  <span class="info-value">{{ resolveVoiceType(detail.typeVoice) }}</span>
                </div>
              </v-col>
              <v-col
                cols="6"
                md="3"
                sm="6"
              >
                <div class="info-field">
                  <span class="info-label">Vel. Discagem</span>
                  <span class="info-value">{{ detail.dialSpeed || '—' }}</span>
                </div>
              </v-col>
              <v-col
                cols="6"
                md="3"
                sm="6"
              >
                <div class="info-field">
                  <span class="info-label">Tentativas</span>
                  <span class="info-value">{{ detail.attempt || '—' }}</span>
                </div>
              </v-col>
            </v-row>
          </template>

          <!-- Webhook -->
          <template v-if="campaign.webhookUrl">
            <v-divider class="my-4" />
            <v-row dense>
              <v-col cols="12">
                <div class="info-field">
                  <span class="info-label">Webhook</span>
                  <span class="info-value text-caption">{{ campaign.webhookUrl }}</span>
                </div>
              </v-col>
            </v-row>
          </template>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-btn
            :disabled="campaign.internalStatus != items.internalStatus[0].value"
            color="error"
            outlined
            @click="$emit('cancel', campaign.id)"
          >
            Cancelar
          </v-btn>
          <v-spacer />
          <v-btn
            :disabled="campaign.internalStatus != items.internalStatus[2].value"
            color="primary"
            outlined
            class="px-5 mr-2"
            @click="$emit('analytic', getService(), campaign.id)"
          >
            <v-icon left>
              {{ icons.mdiChartAreaspline }}
            </v-icon>
            Analítico
          </v-btn>
          <v-btn
            :disabled="campaign.internalStatus != items.internalStatus[2].value"
            color="primary"
            class="px-5"
            @click="downloadReport"
          >
            <v-icon left>
              {{ icons.mdiFileDownload }}
            </v-icon>
            Relatório
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- SMS content dialog -->
    <v-dialog
      v-model="smsContentDialog"
      max-width="560"
    >
      <v-card class="campaign-detail-card">
        <div class="detail-header pa-4 d-flex align-center">
          <div>
            <p class="text-xs text--secondary mb-0">
              Template #{{ detail.smsTemplateId }}
            </p>
            <h3 class="text-h6 font-weight-bold mb-0">
              Mensagem SMS
            </h3>
          </div>
          <v-spacer />
          <v-btn
            icon
            small
            @click="smsContentDialog = false"
          >
            <v-icon color="error">
              {{ icons.mdiClose }}
            </v-icon>
          </v-btn>
        </div>

        <v-divider />

        <v-card-text class="pt-4 pb-5">
          <div
            v-if="smsContentLoading"
            class="d-flex justify-center py-6"
          >
            <v-progress-circular
              indeterminate
              color="primary"
              size="32"
            />
          </div>
          <div
            v-else
            class="sms-message-box pa-4"
          >
            <p
              class="mb-0 text-body-2"
              style="white-space: pre-wrap; line-height: 1.7;"
            >
              {{ smsContent || '—' }}
            </p>
          </div>

          <div class="d-flex align-center mt-3">
            <v-icon
              size="14"
              class="mr-1 text--secondary"
            >
              {{ icons.mdiTextBox }}
            </v-icon>
            <span class="text-xs text--secondary">
              {{ smsContent ? smsContent.length : 0 }} caracteres
            </span>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { ref } from 'vue'
import {
  mdiClose, mdiFileDownload, mdiChartAreaspline, mdiTextBox,
} from '@mdi/js'
import { serviceType as service, campaignStatus, voiceTypes } from '@/utils/enum'

export default {
  props: {
    status: {
      default: false,
      type: Boolean,
    },
    width: {
      default: 900,
      type: Number,
    },
    campaign: {
      type: Object,
      default: () => {},
    },
  },

  setup() {
    const detail = ref({})
    const smsContentDialog = ref(false)
    const smsContentLoading = ref(false)
    const smsContent = ref('')

    const resolveLabel = serviceId => {
      if (serviceId === service.Sanitizer) {
        return { validQuantity: 'Com Atividade', invalidQuantity: 'Sem Atividade' }
      }

      return { validQuantity: 'Enviados', invalidQuantity: 'Não Enviados' }
    }

    const resolveCampaignStatusColor = value => {
      const found = campaignStatus.find(s => s.value === value)

      return found ? found.color : 'error'
    }

    const resolveCampaignStatusLabel = value => {
      const found = campaignStatus.find(s => s.value === value)

      return found ? found.label : 'Não definido'
    }

    const resolveServiceName = serviceId => {
      const map = {
        [service.SMSShortcode]: 'SMS Shortcode',
        [service.SMSToken]: 'SMS Token',
        [service.SMSFlash]: 'SMS Flash',
        [service.SMSMassive]: 'SMS Massivo',
        [service.SMSBet]: 'SMS Bet',
        [service.SMSTokenBet]: 'SMS Token Bet',
        [service.SMSShortBet]: 'SMS Short Bet',
        [service.Voice30s]: 'Voz 30s',
        [service.Voice60s]: 'Voz 60s',
        [service.Sanitizer]: 'Higienizador',
      }

      return map[serviceId] || `Serviço ${serviceId}`
    }

    const resolveServiceChipColor = serviceId => {
      if ([
        service.SMSShortcode,
        service.SMSToken,
        service.SMSFlash,
        service.SMSMassive,
        service.SMSBet,
        service.SMSTokenBet,
        service.SMSShortBet,
      ].includes(serviceId)) return '#06F11A'
      if ([service.Voice30s, service.Voice60s].includes(serviceId)) return '#FFB400'
      if (serviceId === service.Sanitizer) return '#00C853'

      return '#0091EA'
    }

    const resolveVoiceType = type => {
      const found = voiceTypes.find(v => v.value === type)

      return found ? found.text : type
    }

    return {
      detail,
      smsContentDialog,
      smsContentLoading,
      smsContent,
      resolveLabel,
      resolveCampaignStatusColor,
      resolveCampaignStatusLabel,
      resolveServiceName,
      resolveServiceChipColor,
      resolveVoiceType,

      icons: {
        mdiClose,
        mdiFileDownload,
        mdiChartAreaspline,
        mdiTextBox,
      },

      items: {
        services: [],
        internalStatus: campaignStatus,
        voiceTypes,
      },
    }
  },

  async created() {
    await this.getCampaignDetail(this.campaign)
    this.items.services = await this.services()
  },

  methods: {
    ...mapActions('services', {
      services: 'find',
    }),
    ...mapActions('reportServices', {
      downloadSMSReport: 'getSmsPhones',
      downloadSanitizerReport: 'getWhatsAppPhones',
      downloadVoiceReport: 'getVoicePhones',
    }),
    ...mapActions('serviceSms', {
      getSMS: 'findById',
    }),
    ...mapActions('smsTemplates', {
      getSmsTemplate: 'findById',
    }),
    ...mapActions('serviceSanitizer', {
      getSanitizer: 'findById',
    }),
    ...mapActions('serviceVoice', {
      getVoice: 'findById',
    }),

    getService() {
      const { serviceId } = this.campaign

      if ([
        service.SMSShortcode,
        service.SMSToken,
        service.SMSFlash,
        service.SMSMassive,
        service.SMSBet,
        service.SMSTokenBet,
        service.SMSShortBet,
      ].includes(serviceId)) {
        return 'sms'
      }

      if ([service.Voice30s, service.Voice60s].includes(serviceId)) {
        return 'voice'
      }

      if (serviceId === service.Sanitizer) {
        return 'sanitizer'
      }

      return null
    },

    async getCampaignDetail(campaign) {
      const svcType = this.getService()

      if (!svcType) return

      if (svcType === 'sms') {
        const response = await this.getSMS(campaign.id)
        this.detail = response.sms
      }

      if (svcType === 'voice') {
        const response = await this.getVoice(campaign.id)
        this.detail = response.voice
      }
    },

    async openSmsContent(templateId) {
      this.smsContent = ''
      this.smsContentDialog = true
      this.smsContentLoading = true

      try {
        const template = await this.getSmsTemplate(templateId)
        this.smsContent = template?.message || ''
      } finally {
        this.smsContentLoading = false
      }
    },

    async downloadReport() {
      const { id } = this.campaign
      const svcType = this.getService()

      if (svcType === 'sms') this.downloadSMSReport(id)
      if (svcType === 'sanitizer') this.downloadSanitizerReport(id)
      if (svcType === 'voice') this.downloadVoiceReport(id)
    },
  },
}
</script>

<style lang="scss" scoped>
.campaign-detail-card {
  border-radius: 14px !important;
  overflow: hidden;
}

.detail-header {
  background: linear-gradient(135deg, rgba(6, 241, 26, 0.06) 0%, rgba(0, 145, 234, 0.06) 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.info-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  height: 100%;
}

.info-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.5;
  line-height: 1;
}

.info-value {
  font-size: 0.875rem;
  line-height: 1.4;
}

.sms-message-box {
  border-radius: 8px;
  background: rgba(6, 241, 26, 0.04);
  border: 1px solid rgba(6, 241, 26, 0.12);
  min-height: 80px;
  font-family: monospace;
}
</style>
